import { useMutation, useQuery } from '@apollo/client/react';
import { get } from 'lodash-es';
import type { FC, ReactNode } from 'react';

import AnalyticsSection from '@zen/Accounts/AccountForms/CreateAccountForm/AnalyticsSection';
import {
  UpdateAccountMainDetailsDocument,
  type UpdateAccountMainDetailsMutationResult,
  type UpdateAccountMainDetailsMutationVariables
} from '@zen/Accounts/AccountForms/graphql';
import { useUpdateAccountAnalyticsSettings } from '@zen/Accounts/AccountForms/hooks/useUpdateAccountAnalyticsSettings';
import { Form, FormButtons, FormInput } from '@zen/Components';
import type { FormInstance } from '@zen/Components/types';
import { Button, Modal } from '@zen/DesignSystem';
import { useNotification } from '@zen/utils/hooks/useNotification';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performFormMutation } from '@zen/utils/performMutation';

import {
  AnalyticsAccountSettingsDocument,
  type AnalyticsAccountSettingsQueryResult,
  type AnalyticsAccountSettingsQueryVariables
} from '../../GeneralAccountDetails/AnalyticsSettingsDetails/graphql';
import type { UpdateBusinessUnitValues } from './types';
import businessUnitValidation from './updateBusinessUnitModal.validation';

interface Props {
  accountId: string;
  isOpen: boolean;
  name: string;
  onClose: () => void;
  referencePrefix?: string | null;
  showAdminSettings: boolean;
}

const UpdateBusinessUnitModal: FC<Props> = (props) => {
  const { isOpen, onClose, accountId, name, referencePrefix, showAdminSettings } = props;

  const [updateMainDetails] = useMutation<UpdateAccountMainDetailsMutationResult, UpdateAccountMainDetailsMutationVariables>(
    UpdateAccountMainDetailsDocument,
    { refetchQueries: ['getAccountDetails', 'getAccounts'] }
  );
  const { data: analyticsSettingsData } = useQuery<AnalyticsAccountSettingsQueryResult, AnalyticsAccountSettingsQueryVariables>(
    AnalyticsAccountSettingsDocument,
    {
      variables: { accountId },
      fetchPolicy: 'no-cache'
    }
  );
  const { handleUpdateAccountAnalyticsSettings, loading } = useUpdateAccountAnalyticsSettings();
  const { addError, addSuccess } = useNotification();

  const handleUpdateAccount = async (values: UpdateBusinessUnitValues): Promise<IOkOrErrorResult> => {
    const { analyticsSettings, ...rest } = values;

    return performFormMutation({
      mutationFn: () =>
        updateMainDetails({
          variables: {
            input: rest
          }
        }),
      onError: (errors) => addError(get(errors, '[0].message', 'There was a problem updating the business unit.')),
      onSuccess: async (data) => {
        addSuccess('Business unit updated.');

        const businessUnitId: string = data?.updateAccountMainDetails?.accountId || '';

        if (businessUnitId && showAdminSettings) {
          await handleUpdateAccountAnalyticsSettings(businessUnitId, analyticsSettings);
        }
      }
    });
  };

  const renderFormButtons = ({ isSubmitting }: FormInstance<{ members: string[] }>): ReactNode => (
    <FormButtons isSubmitting={isSubmitting} layout="fixed" text="Save changes">
      <Button data-testid="cancel-button" onClick={onClose} variant="ghost">
        Cancel
      </Button>
    </FormButtons>
  );

  const getInitialValues = (): UpdateBusinessUnitValues => ({
    analyticsSettings: {
      analyticsEnabled: analyticsSettingsData?.analyticsAccountSettings?.analyticsEnabled || false,
      analyticsSharedUserEnabled: analyticsSettingsData?.analyticsAccountSettings?.analyticsSharedUserEnabled || false
    },
    referencePrefix,
    tradingName: name,
    accountId
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Edit ${name}`}>
      <Form
        formButtons={renderFormButtons}
        initialValues={getInitialValues()}
        onSubmit={handleUpdateAccount}
        onSuccess={onClose}
        validationSchema={businessUnitValidation}
      >
        {() => {
          return (
            <div className="flex flex-col gap-4">
              <FormInput helperText='For example: "EMEA".' label="Division name" name="tradingName" />
              {showAdminSettings && <AnalyticsSection isDisabled={loading} variant="compact" />}
            </div>
          );
        }}
      </Form>
    </Modal>
  );
};

export default UpdateBusinessUnitModal;
